import React, { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import cn from 'classnames';
import { isEmpty } from 'lodash';

import {
  Button as MUIButton,
  ButtonProps as MUIButtonProps,
  CircularProgress,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';

import styles from './Button.module.scss';

import Tooltip from '../Tooltip';
import YubiIcon from '../YubiIcon';
import { YubiIconTypes } from '../YubiIcon/svgs';

type ButtonSize = 'small' | 'large' | 'medium';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode | string;
  variant?: 'primary' | 'secondary' | 'secondaryDanger' | 'warning' | 'danger' | 'anchor' | 'circle' | 'icon';
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  to?: string;
  nowrap?: boolean;
  loading?: boolean;
  disabled?: boolean;
  tooltipContent?: ReactNode | string;
  icon?: YubiIconTypes;
  iconColor?: string;
  disableMarginRight?: boolean;
  noPadding?: boolean;
  'data-testid'?: string;
  textTransform?: boolean;
  widthPx?: number;
  buttonSize?: ButtonSize;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

function getMUIConfigFromVariant(
  variant: Omit<ButtonProps['variant'], 'circle' | 'icon'>
): Partial<MUIButtonProps> {
  switch (variant) {
    case 'anchor':
      return {
        variant: 'text',
        color: 'secondary',
        disableFocusRipple: true,
        disableRipple: true,
        disableTouchRipple: true,
      };
    case 'secondaryDanger':
      return { variant: 'outlined', color: 'error' };
    case 'danger':
      return { variant: 'contained', color: 'error' };
    case 'primary':
      return { variant: 'contained', color: 'primary' };
    case 'secondary':
      return { variant: 'outlined', color: 'secondary' };
    case 'warning':
      return { variant: 'contained', color: 'warning' };
    default:
      throw new Error(`Button variant, '${variant}' not supported`);
  }
}

const getLinkProps = (to?: string) => (!isEmpty(to) ? { to, component: RouterLink } : {});

function Button({
  name,
  children,
  type,
  widthPx,
  noPadding,
  variant = 'primary',
  className = '',
  onClick = null,
  to = null,
  loading = false,
  disabled = false,
  tooltipContent = null,
  icon = null,
  iconColor = 'white',
  disableMarginRight = false,
  textTransform = false,
  buttonSize = 'medium',
  nowrap = true,
  fullWidth = false,
  ...props
}: ButtonProps) {
  const testProps = {
    'data-testid': props['data-testid'],
  };

  const btnClass = cn(
    {
      [styles['margin-right']]: !disableMarginRight && !tooltipContent,
      [styles['mui-button--text']]: variant === 'anchor',
      [styles['mui-button--icon']]: variant === 'circle',
      [styles.nowrap]: nowrap && !icon,
      [styles['no-padding']]: noPadding,
    },
    className
  );

  if (variant === 'circle') {
    return (
      <IconButton
        className={btnClass}
        onClick={onClick}
        disabled={disabled || loading}
        type={type}
        size={buttonSize}
        {...getLinkProps(to)}
      >
        <span {...testProps}>{children}</span>
      </IconButton>
    );
  }

  if (variant === 'icon') {
    return (
      <IconButton
        className={btnClass}
        onClick={onClick}
        disabled={disabled || loading}
        size={buttonSize}
        type={type}
        {...getLinkProps(to)}
      >
        {icon && <YubiIcon icon={icon} size="md" data-testid={props['data-testid']} />}
        {children && <span {...testProps}>{children}</span>}
      </IconButton>
    );
  }
  let buttonStyles: SxProps<Theme> = {
    my: 2,
    mr: 2,
    textTransform: textTransform ? 'uppercase' : 'none',
  };

  if (fullWidth) {
    buttonStyles.width = '100%';
  } else if (widthPx) {
    buttonStyles.width = `${widthPx}px`;
  } else {
    buttonStyles.width = 'fit-content';
  }

  if (props.sx) {
    buttonStyles = { ...buttonStyles, ...props?.sx };
  }

  return (
    <>
      <MUIButton
        className={btnClass}
        onClick={onClick}
        disabled={disabled || loading}
        size={buttonSize}
        startIcon={
          icon && (
            <YubiIcon
              size="md"
              icon={icon}
              color={loading || disabled ? '#00000042' : iconColor || 'white'}
              display="inline"
            />
          )
        }
        type={type}
        sx={buttonStyles}
        {...getMUIConfigFromVariant(variant)}
        {...getLinkProps(to)}
        {...testProps}
      >
        <div className={styles.buttonContent}>
          {loading && (
            <div>
              <CircularProgress
                size={16}
                sx={{
                  color: '#00000042',
                }}
              />
            </div>
          )}
          <div>{children}</div>
        </div>
      </MUIButton>
      {tooltipContent && (
        <div className={styles['help-icon']}>
          <Tooltip id={name} placement="right" title={tooltipContent} />
        </div>
      )}
    </>
  );
}

export default Button;
